import {
  Stack,
  Text,
  Grid,
  VStack,
  HStack,
  Wrap,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FC } from "react";
import { BlockContainer, IconBox } from "src/atoms";
import Renderer from "src/lib/renderer";
import { Element } from "@prismicio/react";
import { hyperlink } from "src/lib/renderer/elements/hyperlink";
import paragraph from "src/lib/renderer/elements/paragraph";
import { list, oList, listItem } from "src/lib/renderer/elements/list";
import { isValidElement } from "react";
import HeaderMedia from "./components/HeaderMedia";
import { SvgOrImg } from "src/components/SvgOrImg";
import { MAX_NON_CENTERED_IMAGE_WIDTH } from "./constants";
import ShiIcon from "src/atoms/ShiIcon";

export const NAVBAR_HEIGHT = 68;
export const NAVBAR_HEIGHT_MOBILE = 49;

export const PAGE_HEADER_OVERLAPS = {
  overlap: 80,
  no_overlap: undefined,
};

const Header: FC<Gatsby.PageHeaderMigrationFragment> = ({ primary, items }) => {
  if (!primary) throw Error();

  const {
    is_centered,
    buttons,
    content,
    media,
    media_mobile,
    hide_media_on_mobile,
    media_video,
    label_at_the_top,
  } = primary;

  //all breakpoint needs to be specified.
  const isMobile = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: false,
    lg: false,
    xl: false,
    "2xl": false,
    "3xl": false,
  });

  return (
    <BlockContainer
      textAlign={{ base: "left", md: "left" }}
      paddingTop={{ base: "space-16" }}
    >
      <Grid
        gridTemplateColumns={{
          base: "1fr",
          md: is_centered ? "1fr" : "1fr 1fr",
          xl: is_centered ? "1fr" : `1fr ${MAX_NON_CENTERED_IMAGE_WIDTH}px`,
        }}
        gap={{
          base: "space-16",
          md: "space-40",
          "2xl": is_centered ? "space-40" : "space-80",
        }}
        alignItems="stretch"
        bg={{ md: "yellow-0" }}
        borderRadius="intermediate"
        paddingLeft={{ md: "space-40", lg: "space-80" }}
        paddingRight={{
          md: is_centered ? "space-80" : "space-40",
          lg: "space-80",
        }}
        paddingY={{ md: 0 }}
        position="relative"
        marginBottom={{ md: "space-40" }}
        w="full"
      >
        <VStack
          alignItems={{
            base: "center",
            md: is_centered ? "center" : "start",
          }}
          borderRadius="intermediate"
          justifyContent="center"
          textAlign={{
            base: "left",
            md: is_centered ? "center" : "start",
          }}
          align="center"
          maxW="730px"
          spacing={{ base: "space-16", md: "space-40" }}
          bg={{ base: "yellow-0", md: "transparent" }}
          paddingX={{ base: "space-24", md: 0 }}
          paddingY={{ base: "space-24", lg: "58px" }}
          minW="full"
        >
          <VStack
            spacing={{ base: "space-16", md: "space-24" }}
            align="flex-start"
            sx={{
              "h1, h2, h3": {
                marginBottom: 0,
                color: "text-heading",
              },
            }}
            wordBreak="break-word"
          >
            <HStack
              spacing={"space-2"}
              bg="yellow-1"
              alignItems="center"
              padding="8px 20px"
              borderRadius="30px"
            >
              <ShiIcon
                name="trianglealert"
                sx={{
                  path: {
                    stroke: "text-primary",
                    strokeWidth: 0.5,
                  },
                  marginTop: "2px",
                }}
              />
              <Text
                as="span"
                variant="body-sm-reg"
                marginLeft={{ base: "8px" }}
                color="accounting-green-950"
              >
                {label_at_the_top}
              </Text>
            </HStack>
            <Renderer
              field={content}
              overrides={{
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
                [Element.list]: (args) =>
                  list({
                    ...args,
                    overrideProps: {
                      pl: "space-16",
                    },
                  }),
                [Element.oList]: (args) =>
                  oList({
                    ...args,
                    overrideProps: {
                      pl: "space-16",
                    },
                  }),
                [Element.listItem]: (args) =>
                  listItem({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
                [Element.oListItem]: (args) =>
                  listItem({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
              }}
            />
            <Wrap
              direction={{ base: "column" }}
              spacing={{ base: "space-12" }}
              alignSelf={{
                base: "flex-start",
                md: is_centered ? "center" : "flex-start",
              }}
            >
              {items?.map((item) => {
                if (!item) return null;

                return (
                  <HStack key={item.label || ""} spacing={"space-0"}>
                    {item?.icon?.document ? (
                      <IconBox
                        bg={{ base: "yellow-1" }}
                        borderRadius="full"
                        w={{ base: "40px" }}
                        h={{ base: "40px" }}
                        mr={{ md: "space-8" }}
                      >
                        <SvgOrImg
                          imageFile={item.icon?.document?.data?.image_file}
                          size={20}
                        />
                      </IconBox>
                    ) : null}
                    <VStack spacing="space-2" alignItems="flex-start">
                      <Text as="p" variant="body-sm-reg" fontWeight="bold">
                        {item.bold_label}
                      </Text>
                      <Text as="p" variant="body-sm-reg">
                        {item.label}
                      </Text>
                    </VStack>
                  </HStack>
                );
              })}
            </Wrap>
          </VStack>

          <VStack
            spacing={{ base: "space-16", md: 0 }}
            w="full"
            alignItems={{
              base: "center",
              md: is_centered ? "center" : "start",
            }}
          >
            <Stack
              wrap="wrap"
              direction={{ base: "column", md: "row" }}
              alignItems={{ base: "center", md: "center" }}
              textAlign={{
                base: "center",
                md: is_centered ? "center" : "left",
              }}
              spacing={{ base: "space-8", md: 0 }}
              sx={{
                p: {
                  w: { base: "full", md: "auto" },
                  margin: { base: 0 },
                  marginBottom: { md: "24px !important" }, //important is required to override chakra stack default css
                  marginRight: { md: "24px !important" }, //important is required to override chakra stack default css
                },
                ".btn-primary, .btn-secondary": {
                  w: { base: "full", md: "auto" },
                },
              }}
              mx={is_centered ? "auto" : undefined}
            >
              <Renderer
                field={buttons}
                overrides={{
                  [Element.hyperlink]: (args) => {
                    const isSecondaryButton = args?.children.some(
                      (child) =>
                        isValidElement(child) &&
                        child.props.className === "button-secondary",
                    );

                    return hyperlink({
                      ...args,
                      overrideProps: isSecondaryButton
                        ? {
                            variant: "secondary", // before the reskin change the variant was lightGrey
                          }
                        : { hasArrow: true },
                    });
                  },
                }}
              />
            </Stack>

            <HStack spacing={"space-2"} display={{ base: "none", md: "flex" }}>
              <ShiIcon
                name="chevron"
                sx={{
                  path: {
                    stroke: "text-primary",
                    strokeWidth: 0.5,
                  },
                  marginTop: "2px",
                }}
              />
              <Text
                as="span"
                variant="body-sm-reg"
                marginLeft={{ base: "8px" }}
              >
                Fonctionnalités, tarifs et questions fréquentes ci-dessous.
              </Text>
            </HStack>
          </VStack>
        </VStack>

        {media?.document ? (
          <HeaderMedia
            is_centered={is_centered!}
            hide_media_on_mobile={hide_media_on_mobile!}
            media_video={media_video}
            media={
              isMobile ? (media_mobile.document ? media_mobile : media) : media
            }
          />
        ) : null}
      </Grid>
    </BlockContainer>
  );
};

export default Header;
